import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Camera, Upload, Sparkles, RotateCw, ShoppingBag, AlertTriangle,
  Calendar, Check, Crown, Heart, Shield,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { useAuth } from "@/context/AuthContext";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";

// Shared shell for Hair Care + Beard Care coaches. Photo upload → AI analyses
// hair / beard → personalised routine with products + how-to-apply + timeline.

export const BUDGET_BANDS = [
  { value: "under-50",   label: "Under $50" },
  { value: "50-plus",    label: "$50+" },
  { value: "100-plus",   label: "$100+" },
  { value: "250-plus",   label: "$250+" },
  { value: "500-plus",   label: "$500+" },
  { value: "luxury",     label: "Luxury · No budget" },
];

export function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => resolve((r.result || "").toString().split(",").pop());
    r.readAsDataURL(file);
  });
}

function shopHref(product) {
  const q = encodeURIComponent(product);
  return `https://www.google.com/search?tbm=shop&q=${q}`;
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      className={`chip ${active ? "active" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function RoutineStep({ step, label }) {
  return (
    <div className="card" style={{ padding: 14, marginBottom: 10 }} data-testid={`${label}-step-${step.step}`}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div
          style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(232,211,185,0.18)", color: "var(--gold)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {step.step}
        </div>
        <div style={{ flex: 1 }}>
          <div className="h3" style={{ color: "var(--text)", fontSize: 14, lineHeight: 1.3 }}>
            {step.product_type}
          </div>
          {step.why && (
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)", lineHeight: 1.45 }}>
              {step.why}
            </div>
          )}
          {step.how_to_apply && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "var(--surface-soft)",
                borderLeft: "2px solid var(--gold-line)",
              }}
            >
              <div className="eyebrow" style={{ fontSize: 9, marginBottom: 4 }}>HOW TO APPLY</div>
              <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.5 }}>
                {step.how_to_apply}
              </div>
            </div>
          )}
          {step.product_examples?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>TRY ONE OF THESE</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {step.product_examples.map((p, i) => (
                  <a
                    key={i}
                    href={shopHref(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card"
                    style={{
                      padding: "10px 12px",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      textDecoration: "none",
                      background: "var(--bg-elev)",
                      border: "1px solid var(--line)",
                    }}
                    data-testid={`${label}-product-${step.step}-${i}`}
                  >
                    <ShoppingBag size={12} strokeWidth={1.8} color="var(--gold)" />
                    <span style={{ flex: 1, color: "var(--text)", fontSize: 12.5 }}>{p}</span>
                    <span className="tiny" style={{ color: "var(--gold)" }}>Find&nbsp;it →</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GroomingCoachShell({
  feature,        // "hair" | "beard"
  endpoint,       // "/hair/analyze" | "/beard/analyze"
  title,          // "HAIR COACH" | "BEARD COACH"
  hero,           // { eyebrow, h1Pre, h1Em, blurb }
  goalsList,      // [{value, label}, ...]
  goalsKey,       // "hair_goals" | "beard_goals"
  loadingSteps,   // [str, str, ...]
  backRoute,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [goals, setGoals] = useState([]);
  const [budget, setBudget] = useState("50-plus");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFile = (file) => {
    if (!file) return;
    setPickedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
    setResult(null);
  };

  const toggleGoal = (g) =>
    setGoals((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));

  const submit = async () => {
    if (!pickedFile) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const image_base64 = await fileToB64(pickedFile);
      const data = await postWithRetry(
        endpoint,
        { image_base64, image_mime: pickedFile.type || "image/jpeg", goals, budget_band: budget },
        { timeout: 90_000, scope: `grooming-${feature || "photo"}` },
      );
      if (data?.error === "temporary") {
        setError({ type: "server_timeout", retryable: true, message: data.message || "Please try again." });
      } else {
        setResult(data);
        try { track("feature_completed", { feature: "grooming", sub_action: feature || "analysis" }); } catch { /* ignore */ }
      }
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null); setError(null); setPreviewUrl(null); setPickedFile(null);
  };

  return (
    <div className="screen with-nav fade-in" data-testid={`${feature}-screen`}>
      <div className="top-bar">
        <button
          className="icon-btn"
          onClick={() => (result || error) ? reset() : navigate(backRoute || "/improve/grooming")}
          aria-label="Back"
          data-testid={`${feature}-back-btn`}
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">{title}</div>
        {isPremium ? (
          <span className="premium-pill" data-testid={`${feature}-quota-pill`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid={`${feature}-quota-pill`}>FREE · 2/DAY</span>
        )}
      </div>

      {!result && !loading && !error && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{hero.eyebrow}</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 12, lineHeight: 1.12 }}>
            {hero.h1Pre}<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>{hero.h1Em}</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 18 }}>
            {hero.blurb}
          </p>

          <input ref={cameraRef} type="file" accept="image/*" capture="user" style={{ display: "none" }}
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
                 data-testid={`${feature}-camera-input`} />
          <input ref={galleryRef} type="file" accept="image/*" style={{ display: "none" }}
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
                 data-testid={`${feature}-gallery-input`} />

          {previewUrl ? (
            <div style={{ marginBottom: 14, borderRadius: 16, overflow: "hidden", border: "1px solid var(--gold-line)", position: "relative" }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block" }} />
              <button
                type="button"
                onClick={() => { setPickedFile(null); setPreviewUrl(null); }}
                style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", padding: "6px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer" }}
                data-testid={`${feature}-preview-clear`}
              >
                Change photo
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button type="button" className="btn btn-gold" style={{ justifyContent: "center" }}
                      onClick={() => cameraRef.current?.click()} data-testid={`${feature}-camera-btn`}>
                <Camera size={16} /> Take a photo
              </button>
              <button type="button" className="btn btn-outline" style={{ justifyContent: "center" }}
                      onClick={() => galleryRef.current?.click()} data-testid={`${feature}-gallery-btn`}>
                <Upload size={16} /> Upload from gallery
              </button>
            </div>
          )}

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>WHAT MATTERS TO YOU? (PICK ANY)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {goalsList.map((g) => (
                <Chip
                  key={g.value}
                  active={goals.includes(g.value)}
                  onClick={() => toggleGoal(g.value)}
                  testId={`${feature}-goal-${g.value}`}
                >
                  {g.label}
                </Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR BUDGET PER PRODUCT</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {BUDGET_BANDS.map((b) => (
                <Chip
                  key={b.value}
                  active={budget === b.value}
                  onClick={() => setBudget(b.value)}
                  testId={`${feature}-budget-${b.value}`}
                >
                  {b.label}
                </Chip>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-gold"
            disabled={!pickedFile}
            onClick={submit}
            data-testid={`${feature}-analyze-btn`}
            style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
          >
            <Sparkles size={14} /> Build my {feature} plan
          </button>

          <div className="tiny" style={{ marginTop: 12, color: "var(--text-faint)", textAlign: "center" }}>
            Bright, even light · keep the {feature} in focus.
          </div>
        </>
      )}

      {loading && (
        <div style={{ marginTop: 12 }}>
          <AnalyzerProgress totalSeconds={40} testId={`${feature}-analyzing`} steps={loadingSteps} />
          {previewUrl && (
            <div style={{ marginTop: 14, borderRadius: 16, overflow: "hidden", border: "1px solid var(--line)", maxWidth: 200, marginLeft: "auto", marginRight: "auto" }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block" }} />
            </div>
          )}
        </div>
      )}

      {error && !loading && (
        <AnalyzerErrorCard
          error={error}
          onRetry={submit}
          onPickNew={reset}
          feature={feature}
          testId={`${feature}-error`}
        />
      )}

      {result && !loading && (
        <div data-testid={`${feature}-result`}>
          {/* Medical disclaimer banner if applicable */}
          {result.medical_disclaimer && (
            <div
              className="card"
              data-testid={`${feature}-medical-disclaimer`}
              style={{
                padding: 14, marginBottom: 14,
                background: "rgba(232,211,185,0.06)",
                borderLeft: "3px solid var(--gold)",
                display: "flex", gap: 10, alignItems: "flex-start",
              }}
            >
              <Shield size={14} strokeWidth={2} color="var(--gold)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="body-sm" style={{ color: "var(--text-dim)", lineHeight: 1.5 }}>
                {result.medical_disclaimer}
              </div>
            </div>
          )}

          {/* Detected hero */}
          {result.detected && (
            <div className="glow-empty-hero" style={{ padding: 18 }} data-testid={`${feature}-detected`}>
              <div className="glow-empty-badge"><Sparkles size={12} strokeWidth={2} /> WHAT I SEE</div>
              <div className="body" style={{ marginTop: 12, color: "var(--text)", lineHeight: 1.55 }}>
                {result.summary || ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
                {Object.entries(result.detected).filter(([k, v]) => typeof v === "string").slice(0, 6).map(([k, v]) => (
                  <span key={k} className="chip" style={{ pointerEvents: "none", textTransform: "capitalize" }}>
                    {k.replace(/_/g, " ")}: {v}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Whats working */}
          {result.detected?.whats_working?.length > 0 && (
            <div className="outfit-section" data-testid={`${feature}-working`}>
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Heart size={12} strokeWidth={1.8} /> WHAT&apos;S ALREADY WORKING
              </div>
              <div className="card" style={{ padding: 14 }}>
                <ul className="bullet-list" style={{ margin: 0 }}>
                  {result.detected.whats_working.map((w, i) => (
                    <li key={i} style={{ color: "var(--text)" }}>{w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Concerns */}
          {result.detected?.concerns?.length > 0 && (
            <div className="outfit-section" data-testid={`${feature}-concerns`}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>WHAT WE&apos;RE TARGETING</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {result.detected.concerns.map((c, i) => (
                  <span key={i} className="chip" style={{ pointerEvents: "none" }} data-testid={`${feature}-concern-${i}`}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Routine */}
          {result.routine?.products?.length > 0 && (
            <div className="outfit-section" data-testid={`${feature}-routine`}>
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Calendar size={12} strokeWidth={1.8} /> YOUR ROUTINE
                {result.routine.wash_frequency && (
                  <span style={{ marginLeft: 6, color: "var(--gold)", textTransform: "none", letterSpacing: 0, fontSize: 11 }}>
                    · {result.routine.wash_frequency}
                  </span>
                )}
              </div>
              {result.routine.products.map((s, i) => <RoutineStep key={i} step={s} label={feature} />)}
            </div>
          )}

          {/* Weekly extras */}
          {result.routine?.weekly_extras?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10 }}>WEEKLY EXTRAS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {result.routine.weekly_extras.map((w, i) => (
                  <div key={i} className="card" style={{ padding: 14 }}>
                    <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{w.product_type}</div>
                    <div className="tiny" style={{ color: "var(--gold)", marginTop: 2 }}>{w.frequency}</div>
                    {w.how_to_apply && (
                      <div className="body-sm" style={{ marginTop: 8, color: "var(--text-dim)", lineHeight: 1.5 }}>{w.how_to_apply}</div>
                    )}
                    {w.examples?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                        {w.examples.map((p, k) => (
                          <a key={k} href={shopHref(p)} target="_blank" rel="noopener noreferrer" className="chip" style={{ textDecoration: "none", color: "var(--text)" }}>
                            {p}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trimmer recommendation (beard only) */}
          {result.trimmer_recommendation && (
            <div className="outfit-section" data-testid={`${feature}-trimmer`}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>TRIMMER PICKS</div>
              <div className="card" style={{ padding: 14 }}>
                {result.trimmer_recommendation.why_it_matters && (
                  <div className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 10, lineHeight: 1.5 }}>
                    {result.trimmer_recommendation.why_it_matters}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(result.trimmer_recommendation.examples || []).map((p, i) => (
                    <a key={i} href={shopHref(p)} target="_blank" rel="noopener noreferrer"
                       className="card"
                       style={{ padding: "10px 12px", display: "flex", gap: 8, alignItems: "center", textDecoration: "none", background: "var(--bg-elev)", border: "1px solid var(--line)" }}>
                      <ShoppingBag size={12} strokeWidth={1.8} color="var(--gold)" />
                      <span style={{ flex: 1, color: "var(--text)", fontSize: 12.5 }}>{p}</span>
                      <span className="tiny" style={{ color: "var(--gold)" }}>Find it →</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* What to avoid */}
          {result.what_to_avoid?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={12} strokeWidth={1.8} /> WHAT TO AVOID
              </div>
              <ul className="bullet-list">
                {result.what_to_avoid.map((w, i) => <li key={i} style={{ color: "var(--text-dim)" }}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Timeline */}
          {result.results_timeline && (
            <div className="outfit-section" data-testid={`${feature}-timeline`}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>WHEN YOU&apos;LL SEE RESULTS</div>
              <div className="card" style={{ padding: 14 }}>
                <div className="body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{result.results_timeline}</div>
              </div>
            </div>
          )}

          {/* First 30 days */}
          {result.first_30_days && (
            <div className="outfit-section" data-testid={`${feature}-first-30`}>
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Check size={12} strokeWidth={1.8} /> YOUR FIRST 30 DAYS
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{result.first_30_days}</div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-outline"
            onClick={reset}
            data-testid={`${feature}-new-btn`}
            style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
          >
            <RotateCw size={14} /> Analyze a new photo
          </button>
        </div>
      )}
    </div>
  );
}
